import { Button, Col, Form, Modal, notification, Row, Select } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetLieferterminListQuery,
  useSaveLieferterminMutation,
} from "store/masterdata";
import formatDateTime from "helpers/formatDateTime";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";

interface Props {
  initialValue: string;
  kundennummer: number;
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
}

function ChangeLieferterminModal({
  initialValue,
  kundennummer,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { data: lieferterminListData } = useGetLieferterminListQuery();

  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [liefertermin, setLiefertermin] = useState("");

  const [saveLiefertermin, saveLieferterminResult] =
    useSaveLieferterminMutation();

  const handleSubmit = () => {
    setConfirmationModalVisible(true);
  };

  const lieferterminOptions = lieferterminListData
    ? lieferterminListData.map((item: string) => ({
        label: formatDateTime({
          date: new Date(item),
          withoutTime: true,
          isLocalTimezone: true,
        }),
        value: item,
      }))
    : [];

  const onChange = (value: string) => {
    if (liefertermin !== value) {
      setLiefertermin(value);
    }
  };

  const confirmChangeLiefertermin = async () => {
    try {
      const response = await saveLiefertermin({
        liefertermin,
        kundennummer,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.set_liefertermin_success"),
        });
        onClose();
        onSuccess();
      } else {
        notification.error({ message: response.errorMessage });
        onClose();
      }
    } catch (error) {
      console.error("Error", error);
      onClose();
    }
  };

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.change_liefertermin")}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        id="change-liefertermin-form"
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Form.Item name="option" rules={[{ required: true }]}>
          <Select
            defaultValue={
              initialValue
                ? formatDateTime({
                    date: new Date(initialValue),
                    withoutTime: true,
                    isLocalTimezone: true,
                  })
                : null
            }
            options={lieferterminOptions}
            placeholder={t("masterdata.lw_details.liefertermin")}
            onChange={onChange}
          />
        </Form.Item>
        <Row gutter={20}>
          <Col span={12}>
            <Button onClick={onClose} block>
              {t("abort")}
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" htmlType="submit" block>
              {t("confirm")}
            </Button>
          </Col>
        </Row>
      </Form>
      <ConfirmationModal
        visible={confirmationModalVisible}
        isLoading={saveLieferterminResult.isLoading}
        title={t(
          "masterdata.lw_details.change_liefertermin_confirmation_title"
        )}
        description={t(
          "masterdata.lw_details.change_liefertermin_confirmation"
        )}
        onClose={() => setConfirmationModalVisible(false)}
        onConfirm={() => confirmChangeLiefertermin()}
      />
    </Modal>
  );
}

export default ChangeLieferterminModal;
